import React, { forwardRef, Ref, useState, useEffect } from 'react'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  Avatar,
  Text,
  AspectRatio,
  Image,
  Modal,
  Drawer,
  FocusTrap,
} from '@mantine/core'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { IconTrash, IconMessageCircle2 } from '@tabler/icons-react'
import { ArenaPost } from './api'
import { LeaguestarState } from '../state'
import { Typography } from '../common/typography'
import Reactions from '../socials/Reactions'
import DeletePost from './DeletePost'
import Comments from './Comments'
import classes from './Arena.module.css'

export default forwardRef(function RoarPost(
  {
    post_id,
    arenaPost,
    isEditing,
    showCommentsInline = false,
    initialCommentToScroll = null,
  }: {
    post_id: number
    arenaPost: ArenaPost
    isEditing?: number
    showCommentsInline?: boolean
    initialCommentToScroll?: number | null
  },
  ref: Ref<HTMLDivElement>
) {
  const { user_id } = LeaguestarState.useState((state) => ({
    user_id: state.user_id,
  }))

  const [commentsOpen, setCommentsOpen] = useState<boolean>(false)
  const [
    commentsModalOpen,
    { open: openCommentsModal, close: closeCommentsModal },
  ] = useDisclosure(false)
  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)
  const isDesktop = useMediaQuery('(min-width: 800px)')

  useEffect(() => {
    if (initialCommentToScroll) {
      setCommentsOpen(true)

      setTimeout(() => {
        const commentElement = document.getElementById(
          `comment-${initialCommentToScroll}`
        )
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 500)
    }
  }, [initialCommentToScroll])

  return (
    <div>
      <div
        ref={ref}
        className={clsx(
          classes.roarDetailsCard,
          {
            [classes.blurred]: !!isEditing,
          },
          { [classes.roarComments]: commentsOpen && isDesktop }
        )}
      >
        <div className={classes.roarDetailsContainer}>
          <div className={classes.roarDetailsAuthor}>
            <div>
              <Avatar
                color="#F8F9FA"
                radius="xl"
                src={arenaPost.user_photo}
              ></Avatar>
              <div>
                <p>{`${arenaPost.user_first_name} ${arenaPost.user_last_name}`}</p>
                <p className={classes.roarDate}>
                  {new Date(arenaPost.created_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>
            {user_id === arenaPost.user_id && (
              <div>
                <IconTrash
                  className={classes.roarDetailsIcon}
                  onClick={openDeleteModal}
                />
              </div>
            )}
          </div>
          {arenaPost.text && (
            <Text
              component="h4"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(arenaPost.text),
              }}
            />
          )}
          {(arenaPost.image.main || arenaPost.video.main) && (
            <AspectRatio ratio={16 / 9} style={{ marginTop: '12px' }}>
              {arenaPost.image.main ? (
                <Image src={arenaPost.image.main} fit="cover" />
              ) : arenaPost.video.main ? (
                <video
                  src={arenaPost.video.main}
                  controls
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : null}
            </AspectRatio>
          )}
        </div>
        <div className={classes.roarPostFooter}>
          <div className={classes.roarDetailsSocials}>
            <div
              className={classes.postComments}
              onClick={() =>
                showCommentsInline
                  ? setCommentsOpen(!commentsOpen)
                  : openCommentsModal()
              }
            >
              <IconMessageCircle2
                className={clsx(classes.socialsIcon, classes.roarComment)}
              />
              {arenaPost.comment_count > 0 && (
                <Typography
                  fs="xs"
                  fc={{
                    light: 'white',
                    dark: 'white',
                  }}
                  lh="150"
                  className={classes.counter}
                >
                  {arenaPost.comment_count}
                </Typography>
              )}
            </div>
            <Reactions
              post_id={arenaPost.post_id}
              reactions={arenaPost.reactions}
              isRoar={arenaPost.is_roar}
            />
          </div>
          <div className={classes.roarBanner}>Roar</div>
        </div>
      </div>

      {showCommentsInline && commentsOpen && <Comments post_id={post_id} />}

      <DeletePost
        post_id={post_id}
        openedDeleteModal={openedDeleteModal}
        closeDeleteModal={closeDeleteModal}
      />

      {isDesktop ? (
        <Modal.Root
          opened={commentsModalOpen}
          onClose={closeCommentsModal}
          centered
          size="lg"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{ header: classes.header, body: classes.body }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <FocusTrap.InitialFocus />
            <Modal.Header>
              <Modal.Title>
                <Typography fw={500} lh="125">
                  Comments
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Comments post_id={post_id} />
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={commentsModalOpen}
          onClose={closeCommentsModal}
          position="bottom"
          classNames={{
            header: classes.header,
            body: classes.body,
            content: classes.commentsContent,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <FocusTrap.InitialFocus />
            <Drawer.Header>
              <Drawer.Title>
                <Typography fs="lg" fw={500} lh="125">
                  Comments
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              <Comments post_id={post_id} />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  )
})
